import { SEOMeta } from '../components/common/SEOMeta';
import { SectionTitle } from '../components/common/SectionTitle';
import { Button } from '../components/common/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Link } from 'wouter';
import { ROUTES } from '../routes/Routes';
import '../assets/css/pages/Services.css';

export default function Services() {
  const headerReveal = useScrollReveal();
  const servicesReveal = useScrollReveal();

  const services = [
    {
      id: 1,
      icon: 'fa-globe',
      title: 'Web Development',
      desc: 'We engineer high-performance, scalable web applications using modern frameworks. From complex SPAs to enterprise portals, we ensure flawless execution.',
      features: ['Custom Web Apps', 'Progressive Web Apps', 'CMS Development']
    },
    {
      id: 2,
      icon: 'fa-mobile-screen',
      title: 'Mobile App Dev',
      desc: 'Native and cross-platform mobile solutions that deliver premium user experiences. We build robust apps that drive engagement.',
      features: ['iOS Native (Swift)', 'Android (Kotlin)', 'React Native / Flutter']
    },
    {
      id: 3,
      icon: 'fa-code',
      title: 'Software Dev',
      desc: 'End-to-end custom software development tailored to your specific business logic. We design architectures that scale.',
      features: ['Enterprise Software', 'API Development', 'Legacy Modernization']
    },
    {
      id: 4,
      icon: 'fa-pen-nib',
      title: 'UI/UX Design',
      desc: 'Data-driven design that marries aesthetics with usability. We create interfaces that look beautiful and function intuitively.',
      features: ['User Research', 'Prototyping', 'Usability Testing']
    },
    {
      id: 5,
      icon: 'fa-cloud',
      title: 'Cloud Solutions',
      desc: 'Future-proof your infrastructure with our cloud services. We handle architecture, migration, and management on leading providers.',
      features: ['AWS / Azure / GCP', 'Cloud Migration', 'Serverless Architecture']
    },
    {
      id: 6,
      icon: 'fa-lightbulb',
      title: 'IT Consulting',
      desc: 'Strategic technology guidance to align your IT infrastructure with your business goals and digital transformation.',
      features: ['Digital Strategy', 'Architecture Review', 'Tech Stack Selection']
    },
    {
      id: 7,
      icon: 'fa-chart-line',
      title: 'Digital Marketing',
      desc: 'Amplify your digital presence with targeted strategies. We combine technical SEO, analytics, and growth marketing.',
      features: ['Technical SEO', 'Performance Marketing', 'Conversion Optimization']
    },
    {
      id: 8,
      icon: 'fa-shield-halved',
      title: 'Cybersecurity',
      desc: 'Protect your digital assets with our comprehensive security solutions against modern threats and ensure compliance.',
      features: ['Security Audits', 'Data Encryption', 'Compliance Management']
    },
    {
      id: 9,
      icon: 'fa-robot',
      title: 'AI & ML',
      desc: 'Leverage artificial intelligence and machine learning to automate processes and derive actionable insights from your data.',
      features: ['Predictive Analytics', 'Natural Language Processing', 'Computer Vision']
    },
    {
      id: 10,
      icon: 'fa-infinity',
      title: 'DevOps',
      desc: 'Streamline your development lifecycle with automated CI/CD pipelines, containerization, and infrastructure as code.',
      features: ['CI/CD Pipelines', 'Container orchestration', 'Infrastructure as Code']
    },
    {
      id: 11,
      icon: 'fa-cart-shopping',
      title: 'E-Commerce',
      desc: 'Build scalable digital storefronts that convert visitors into loyal customers, featuring seamless payment integrations.',
      features: ['Headless Commerce', 'Payment Gateways', 'Inventory Management']
    },
    {
      id: 12,
      icon: 'fa-house-signal',
      title: 'IoT Solutions',
      desc: 'Connect your physical devices to the digital world. We build robust IoT ecosystems with real-time data processing.',
      features: ['Device Integration', 'Real-time Dashboards', 'Edge Computing']
    }
  ];

  return (
    <>
      <SEOMeta
        title="Services"
        description="Comprehensive IT services including web development, mobile apps, cloud solutions, and IT consulting by Logicraft Infotech."
        canonical="https://www.logicraftinfotech.com/service"
      />

      <section className="page-header" ref={headerReveal.ref}>
        <div className={`container ${headerReveal.className}`}>
          <SectionTitle title="Our Services" centered />
          <p className="page-header-desc">
            We deliver end-to-end technology solutions. From initial concept to deployment and scaling, our engineering teams provide the expertise needed to bring complex digital products to life.
          </p>
        </div>
      </section>

      <section className="section" ref={servicesReveal.ref}>
        <div className={`container ${servicesReveal.className}`}>
          <div className="services-grid-full">
            {services.map((service, index) => (
              <div
                className="service-detail-card"
                key={service.id}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="service-icon-circle-large">
                  <i className={`fa-solid ${service.icon}`}></i>
                </div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
                <ul className="service-features-list">
                  {service.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="service-cta glass-card">
            <h2>Need a custom <span className="gradient-text">solution?</span></h2>
            <p>
              Every business is unique. We specialize in building tailored systems that match your exact operational requirements. Let's engineer something great together.
            </p>
            <Link href={ROUTES.CONTACT.path}>
              <button className="btn-solid">Discuss Your Project</button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}